import React, { PureComponent } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import Big from 'big.js';
import classNames from 'classnames';

import { BaseInputProps } from '../types';
import { isExpired, isNullLike } from '../utils';

import { Expiries } from './Expiries';
import { Num } from './Num';
import { SettleContext } from '../settleContext';

export interface ExpiryPercentageValueInfo {
  expiry: string;
  value?: number;
}

export interface ExpiryPercentageValueProps extends BaseInputProps {
  value?: ExpiryPercentageValueInfo[];

  onChange?: (val: ExpiryPercentageValueInfo[]) => void;

  options?: string[];
}

export class ExpiriesPercentageValueList extends PureComponent<ExpiryPercentageValueProps> {
  // 结算上下文：由 ValueDefaultsSelector 按 GLOBAL/ASSET/BOT 作用域注入，用于 expiry 到期判断
  static contextType = SettleContext;
  declare context: React.ContextType<typeof SettleContext>;

  value: ExpiryPercentageValueInfo[] = [];

  isEmptyItem = (item: ExpiryPercentageValueInfo) => {
    return !item.expiry?.trim() && isNullLike(item.value);
  };

  onChange = (i: number, val: any, key?: keyof ExpiryPercentageValueInfo) => {
    const value = [...this.value];
    if (!value[i]) value[i] = { expiry: '' };
    if (key) value[i][key] = val as never;
    else value[i] = { ...value[i], ...val };
    this.props.onChange?.(
      value.length === 1 && this.isEmptyItem(value[0]) ? [] : value,
    );
  };

  onAdd = (i: number) => {
    const value = [...this.value];
    value.splice(i + 1, 0, { expiry: '' });
    this.props.onChange?.(value);
    this.props.onBlur?.();
  };

  onDel = (i: number) => {
    const value = [...this.value];
    value.splice(i, 1);
    this.props.onChange?.(
      value.length === 1 && this.isEmptyItem(value[0]) ? [] : value,
    );
    this.props.onBlur?.();
  };

  render() {
    this.value = this.props.value?.filter(it => !isExpired(it.expiry, this.context)) || [];
    // if (!this.value.length && !this.props.readonly)
    //   this.value.push({ expiry: '' });
    return (
      <div
        className={classNames(
          'param-expiries-limit',
          { readonly: this.props.readonly },
          this.props.className,
        )}
        style={this.props.style}
      >
        {this.props.readonly ? (
          this.props.value?.length ? (
            this.value
              .filter(it => it.expiry)
              .map((it, i) => (
                <span key={i}>
                  {it.expiry}
                  <span>/</span>
                  {!isNullLike(it.value)
                    ? `${+Big(Number(it.value)).times(100)}%`
                    : '-'}
                    <br/>
                </span>
              ))
          ) : (
            '-'
          )
        ) : (
          <div className="grid grid-cols-6 gap-4 w-full p-2">
            {/* 表头 */}
            <div className="font-medium text-gray-700 rounded-md">Expiry</div>
            <div className="font-medium text-gray-700 rounded-md">Value</div>
            <div className="rounded-md"></div>
            <div className="rounded-md"></div>
            <div className="rounded-md"></div>
            {
              this.value.length > 0 ? <div className='rounded-md'></div> : <div className="flex items-center space-x-2">
              <button
                onClick={() => this.onAdd(0)}
                className="p-1 rounded-full hover:bg-blue-100 transition-colors"
                aria-label="Add item"
              >
                <PlusOutlined />
              </button>
            </div>
            }
            
            {/* 数据行 */}
            {this.value.map((it, i) => (
              <React.Fragment key={i}>
                {/* Expiry列 */}
                <div className="flex items-center col-span-2">
                  <Expiries
                    className="w-full max-w-[15rem]"
                    multiple={false}
                    options={this.props.options}
                    value={[it.expiry]}
                    onChange={val => this.onChange(i, val[0], 'expiry')}
                    calcDisabled={expiry =>
                      this.value.some(
                        item => item !== it && item.expiry === expiry,
                      )
                    }
                    onBlur={this.props.onBlur}
                  />
                </div>
                
                {/* Value列 */}
                <div className="flex items-center col-span-2">
                  <Num
                    className="w-full max-w-[11.25rem] h-8"
                    value={it.value}
                    onChange={val => this.onChange(i, { value: val })}
                    onBlur={this.props.onBlur}
                    isPercentage
                  />
                </div>
                
                {/* 空列 */}
                <div></div>
                
                {/* 操作列 */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => this.onAdd(i)}
                    className="p-1 rounded-full hover:bg-blue-100 transition-colors"
                    aria-label="Add item"
                  >
                    <PlusOutlined />
                  </button>
                  <button
                    onClick={() => this.onDel(i)}
                    className="p-1 rounded-full hover:bg-red-100 transition-colors"
                    aria-label="Delete item"
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  }
}
